import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { ageFromBirth } from "../lib/calc";
import { Plus, Search, Baby, FileText, X, FileUp, Loader2, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const empty = { name: "", sex: "M", birth_date: "", curp: "", nationality: "Mexicana", gestational_weeks: 40, blood_type: "", allergies: "", address: "", phone: "", guardian_name: "", guardian_phone: "", guardian_relationship: "", email: "", notes: "" };

export default function Patients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const cdaInputRef = useRef(null);

  const onImportCDA = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/patients/import-cda", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Paciente importado: ${res.data.name}`);
      navigate(`/pacientes/${res.data.patient_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo importar el XML");
    } finally { setImporting(false); }
  };

  const load = () => api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Paciente de primera vez agendado: prellenar la ficha con su pre-registro.
  const [params, setParams] = useSearchParams();
  const fromAppt = params.get("cita");
  useEffect(() => {
    if (!fromAppt) return;
    api.get(`/appointments/${fromAppt}/intake`).then(({ data }) => {
      const it = data.intake || {};
      const pr = data.prospect || {};
      const extra = [it.medications && `Medicamentos actuales: ${it.medications}`, it.reason && `Motivo (pre-registro): ${it.reason}`].filter(Boolean).join(". ");
      setForm({
        ...empty, name: it.patient_name || data.patient_name || "", sex: it.sex || "M", birth_date: it.birth_date || "",
        curp: it.curp || "", address: it.address || "", guardian_name: it.guardian_name || "",
        guardian_relationship: it.guardian_relationship || "", email: it.email || pr.email || "",
        phone: it.guardian_name ? "" : (it.phone || pr.phone || ""), guardian_phone: it.guardian_name ? (it.phone || pr.phone || "") : "",
        blood_type: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].includes(it.blood_type) ? it.blood_type : "",
        allergies: it.allergies || "", pathological_history: it.conditions || "", notes: extra,
      });
      setOpen(true);
      if (!data.intake) toast.info("El paciente aún no llena su pre-registro; completa la ficha manualmente.");
    }).catch(() => toast.error("No se encontró la cita"));
  }, [fromAppt]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.name || !form.birth_date) { toast.error("Nombre y fecha de nacimiento son obligatorios"); return; }
    setSaving(true);
    try {
      const res = await api.post("/patients", form);
      if (fromAppt) {
        await api.post(`/appointments/${fromAppt}/link`, { patient_id: res.data.patient_id }).catch(() => {});
        setParams({});
      }
      toast.success("Expediente creado");
      setOpen(false); setForm(empty);
      navigate(`/pacientes/${res.data.patient_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear paciente");
    } finally { setSaving(false); }
  };

  const filtered = patients.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-extrabold text-brand-ink tracking-tight">Pacientes</h1>
          <p className="text-muted-foreground">Expedientes guardados para futuras consultas.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={cdaInputRef} type="file" accept=".xml,application/xml,text/xml" className="hidden" onChange={onImportCDA} data-testid="import-cda-input" />
          <button data-testid="import-cda-btn" onClick={() => cdaInputRef.current?.click()} disabled={importing}
            className="flex items-center gap-2 bg-white border border-stone-200 text-brand-ink font-outfit font-semibold px-4 py-3 rounded-2xl transition-all hover:bg-muted disabled:opacity-60" title="Importar paciente desde XML HL7 CDA (NOM-024)">
            {importing ? <Loader2 size={18} className="animate-spin" /> : <FileUp size={18} />} Importar XML
          </button>
          <button data-testid="open-new-patient" onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold px-5 py-3 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-brand-terracotta/20">
            <Plus size={18} /> Nuevo paciente
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <input data-testid="patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar paciente…"
          className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-stone-100 focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40 font-nunito" />
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><div className="w-9 h-9 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-3xl border border-stone-100">
          <div className="w-16 h-16 rounded-3xl bg-brand-terracotta/10 flex items-center justify-center mx-auto mb-4">
            <Baby className="text-brand-terracotta" size={32} />
          </div>
          <p className="text-brand-ink font-outfit font-semibold">No hay pacientes</p>
          <p className="text-muted-foreground text-sm mt-1">Crea tu primer expediente.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {filtered.map((p, i) => (
              <motion.button key={p.patient_id} data-testid={`patient-item-${p.patient_id}`}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                onClick={() => navigate(`/pacientes/${p.patient_id}`)}
                className="group bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100 text-left transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(61,64,91,0.08)]">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-outfit font-bold text-white text-lg ${p.sex === "F" ? "bg-brand-terracotta" : "bg-brand-sage"}`}>
                    {p.name?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-outfit font-bold text-brand-ink truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.record_number}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4 text-sm">
                  <span className="text-brand-ink/70 font-semibold">{ageFromBirth(p.birth_date).text}</span>
                  <span className="flex items-center gap-1.5 text-muted-foreground"><FileText size={14} />{p.consultation_count || 0} consultas</span>
                </div>
                {p._offline && (
                  <div className="mt-3 text-xs bg-amber-100 text-amber-700 font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1" data-testid={`patient-offline-${p.patient_id}`}>
                    <CloudOff size={12} /> Pendiente de subir
                  </div>
                )}
                {p.allergies && (
                  <div className="mt-3 text-xs bg-destructive/10 text-destructive font-semibold px-2.5 py-1 rounded-full inline-block">
                    Alergias: {p.allergies}
                  </div>
                )}
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-brand-ink">Nuevo expediente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Nombre completo *</Label>
              <Input data-testid="input-patient-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" placeholder="Ej. Sofía Pérez" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Sexo *</Label>
                <div className="flex gap-2 mt-1.5">
                  {[{ v: "M", l: "Masculino" }, { v: "F", l: "Femenino" }].map((s) => (
                    <button key={s.v} type="button" data-testid={`sex-${s.v}`} onClick={() => setForm({ ...form, sex: s.v })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${form.sex === s.v ? "bg-brand-terracotta text-white" : "bg-muted text-brand-ink/60"}`}>
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Nacimiento *</Label>
                <Input data-testid="input-patient-birth" type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="rounded-xl mt-1.5" />
              </div>
            </div>
            {!(form.birth_date && ageFromBirth(form.birth_date).years >= 18) && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Semanas de gestación al nacer (para edad corregida)</Label>
              <Select value={String(form.gestational_weeks)} onValueChange={(v) => setForm({ ...form, gestational_weeks: parseInt(v) })}>
                <SelectTrigger data-testid="input-patient-gestation" className="rounded-xl mt-1.5 bg-white">
                  <SelectValue placeholder="Selecciona las semanas" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 40 - 26 + 1 }, (_, i) => 26 + i).map((w) => (
                    <SelectItem key={w} value={String(w)} data-testid={`gestation-opt-${w}`}>{w} semanas{w === 40 ? " (a término)" : w < 37 ? " (prematuro)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">{form.gestational_weeks < 37 ? "Prematuro: se calculará edad corregida hasta los 2 años." : "A término."}</p>
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">CURP (NOM-024)</Label>
                <Input data-testid="input-patient-curp" value={form.curp} onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })} maxLength={18} className="rounded-xl mt-1.5 uppercase" placeholder="18 caracteres" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Nacionalidad</Label>
                <Input data-testid="input-patient-nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className="rounded-xl mt-1.5" placeholder="Mexicana" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Tipo de sangre</Label>
                <Input value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} className="rounded-xl mt-1.5" placeholder="O+" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Alergias</Label>
                <Input data-testid="input-patient-allergies" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="rounded-xl mt-1.5" placeholder="Ninguna" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Domicilio (NOM-004)</Label>
                <Input data-testid="input-patient-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl mt-1.5" placeholder="Calle, número, colonia, ciudad" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Teléfono del paciente</Label>
                <Input data-testid="input-patient-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Madre/Padre/Tutor</Label>
                <Input value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} className="rounded-xl mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Parentesco</Label>
                <Input data-testid="input-guardian-rel" value={form.guardian_relationship} onChange={(e) => setForm({ ...form, guardian_relationship: e.target.value })} className="rounded-xl mt-1.5" placeholder="Madre" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Tel. tutor</Label>
                <Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} className="rounded-xl mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Correo del paciente o tutor</Label>
              <Input data-testid="input-patient-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5" placeholder="Para enviarle confirmación y recordatorio de sus citas" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Notas adicionales</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1.5" rows={2} />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Los antecedentes heredofamiliares y personales se capturan en la pestaña <b>Historia clínica</b> del expediente.</p>
            <button data-testid="submit-patient" onClick={submit} disabled={saving}
              className="w-full bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3 rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60">
              {saving ? "Guardando…" : "Crear expediente"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
